// How common a field of a data model is, and whether it has stopped coming: from the model the Vault
// reports (per field: documents that have it, last seen; per data model: documents, last seen).

export type FieldKind = 'always' | 'common' | 'occasional' | 'rare';
export type KindFilter = 'all' | 'common' | 'occasional' | 'rare' | 'stale';
export const KIND_FILTERS: KindFilter[] = ['all', 'common', 'occasional', 'rare', 'stale'];

export interface FieldStat {
  /** Share of the data model's documents that have the field (0..1). */
  coverage: number;
  kind: FieldKind;
  /** The field stopped coming while the data model goes on. */
  stale: boolean;
  /** How long the data model has had newer documents than the field's last one, ms. */
  staleFor: number;
}

const HOUR = 3_600_000;

/** always ≥ 95 %, common ≥ 50 %, occasional ≥ 5 %, rare below that (or seen in a handful of documents only). */
export function kindOf(coverage: number, count: number): FieldKind {
  if (count <= 3 && coverage < 0.5) return 'rare';
  if (coverage >= 0.95) return 'always';
  if (coverage >= 0.5) return 'common';
  if (coverage >= 0.05) return 'occasional';
  return 'rare';
}

export function statOf(
  field: { count: number; lastSeen: number | null },
  cls: { messages: number; firstSeen: number | null; lastSeen: number | null },
): FieldStat {
  const coverage = cls.messages > 0 ? Math.min(1, field.count / cls.messages) : 0;
  // A field is stale when documents of the data model went on far longer than they usually take
  // (twenty average intervals, at least an hour) after the field's last one.
  let staleFor = 0;
  let stale = false;
  if (field.lastSeen !== null && cls.lastSeen !== null && cls.firstSeen !== null && cls.messages > 1) {
    staleFor = Math.max(0, cls.lastSeen - field.lastSeen);
    const usual = (cls.lastSeen - cls.firstSeen) / cls.messages;
    stale = staleFor > Math.max(HOUR, 20 * usual);
  }
  return { coverage, kind: kindOf(coverage, field.count), stale, staleFor };
}

export const matchesKind = (s: FieldStat, f: KindFilter): boolean =>
  f === 'all' || (f === 'stale' ? s.stale : f === 'common' ? s.kind === 'always' || s.kind === 'common' : s.kind === f);

export function kindCounts(stats: FieldStat[]): Record<KindFilter, number> {
  const out = { all: stats.length, common: 0, occasional: 0, rare: 0, stale: 0 };
  for (const s of stats) {
    if (s.kind === 'always' || s.kind === 'common') out.common++;
    else out[s.kind]++;
    if (s.stale) out.stale++;
  }
  return out;
}

export const KIND_LABEL: Record<FieldKind, string> = { always: 'In every document', common: 'Common', occasional: 'Occasional', rare: 'Rare' };

/** Percent for display: "<1 %" below one, no decimals otherwise. */
export const percent = (c: number): string => (c > 0 && c < 0.01 ? '<1 %' : `${Math.round(c * 100)} %`);
